import React, { useEffect, useRef } from 'react';
import { useEnteredView } from '@fiverr-private/hooks';
import { Heading } from '@fiverr-private/typography';
import { Container, Stack } from '@fiverr-private/layout_components';
import DragScrollCarousel from '../DragScrollCarousel';
import { translate } from '../../shared/helpers';
import { sendBiankaImpression, sendMixpanelEvent } from '../../shared/biEvents';
import { EVENT_NAMES } from '../../shared/biEvents/eventNames';
import { addScrollSpeedMotion } from '../../shared/animations';
import TestimonialCard from './TestimonialCard';
import { TESTIMONIALS_FIRST_COLUMN, TESTIMONIALS_SECOND_COLUMN, TESTIMONIALS_THIRD_COLUMN } from './constants';
import * as styles from './styles.ve.css';

const LIMIT_OF_TESTIMONIALS_FOR_DESKTOP = 2;

const Testimonials: React.FC<{ isV16?: boolean; isBrandPage?: boolean }> = ({ isV16 = false, isBrandPage = false }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const innerContentRef = useRef<HTMLDivElement>(null);

  useEnteredView(innerContentRef, {
    threshold: 1,
    onEntered: () => {
      sendBiankaImpression({
        eventName: EVENT_NAMES.BIANKA.TESTIMONALS_IS_SHOWN,
        componentName: 'testimonals',
        componentText: 'Testimonials',
        isV16,
      });

      sendMixpanelEvent({
        eventName: EVENT_NAMES.MIXPANEL.LOHP_TESTIMONIALS_IS_SHOWN,
        isV16,
      });
    },
  });

  useEffect(() => {
    const cleanupScrollSpeedMotion = addScrollSpeedMotion(containerRef.current as HTMLElement);

    return () => {
      cleanupScrollSpeedMotion();
    };
  }, []);

  return (
    <Stack
      direction="column"
      alignItems="center"
      paddingTop={{
        default: '12',
        sm: '16',
        md: '20',
      }}
      paddingBottom={{
        default: '4',
        md: '0',
      }}
      paddingLeft={{
        default: '5',
        sm: '8',
      }}
      paddingRight={{
        default: '5',
        sm: '8',
      }}
      gap={{
        default: '6',
        sm: '14',
        md: '12',
        lg: '24',
      }}
      overflow="hidden"
      className={styles.testimonialsWrapper}
      ref={containerRef}
    >
      <Heading
        as="h2"
        textAlign="center"
        width="100%"
        size={{ sm: 'h_xxl' }}
        className={styles.testimonialsTitle}
        ref={innerContentRef}
      >
        {isBrandPage ? translate('testimonials.title_brand_page') : translate('testimonials.title')}
      </Heading>
      <Container
        height={{ default: '366px', sm: '374px', md: '388px', lg: '888px', xl: '976px' }}
        minHeight="fit-content"
        width="100%"
        position="relative"
        className={styles.testimonialsCarouselContainer}
      >
        <Container hidden={{ default: false, lg: true }}>
          <DragScrollCarousel gap="6" className={styles.testimonialsCarousel} paddingSpacers>
            {[...TESTIMONIALS_FIRST_COLUMN, ...TESTIMONIALS_SECOND_COLUMN, ...TESTIMONIALS_THIRD_COLUMN].map(
              (testimonial) => (
                <TestimonialCard key={testimonial.author} {...testimonial} />
              )
            )}
          </DragScrollCarousel>
        </Container>
        <Container hidden={{ default: true, lg: false }} zIndex="1">
          <Stack direction="row" gap="8" className={styles.testimonialsCarousel} justifyContent={{ lg: 'center' }}>
            <Stack
              direction="column"
              gap="8"
              paddingTop={{ lg: '20' }}
              data-speed="1.25"
              className={styles.testimonialsCarouselColumn}
              zIndex="2"
            >
              {TESTIMONIALS_FIRST_COLUMN.map((testimonial) => (
                <TestimonialCard key={testimonial.author} {...testimonial} />
              ))}
            </Stack>
            <Stack direction="column" gap="8" data-speed="3">
              {TESTIMONIALS_SECOND_COLUMN.slice(0, LIMIT_OF_TESTIMONIALS_FOR_DESKTOP).map((testimonial) => (
                <TestimonialCard key={testimonial.author} {...testimonial} />
              ))}
            </Stack>
            <Stack
              direction="column"
              gap="8"
              data-speed="1.25"
              paddingTop={{ lg: '20' }}
              className={styles.testimonialsCarouselColumn}
            >
              {TESTIMONIALS_THIRD_COLUMN.map((testimonial) => (
                <TestimonialCard key={testimonial.author} {...testimonial} />
              ))}
            </Stack>
          </Stack>
        </Container>
        <Container
          position="absolute"
          bottom="-5px"
          left="-10px"
          right="-10px"
          hidden={{ default: true, lg: false }}
          className={styles.testimonialsBootomFade}
          height={{ lg: '235px', xl: '250px' }}
          zIndex="2"
        />
      </Container>
    </Stack>
  );
};

export default Testimonials;
